
import { useState } from "react";
import { useMagicLink } from "@/hooks/useMagicLink";
import { useNavigate } from "react-router-dom";
import { User, Mail, ArrowLeft } from "lucide-react";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  CardFooter 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function MagicLinkLogin() {
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);
  const { sendMagicLink, loading } = useMagicLink();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await sendMagicLink(email);
    if (result) {
      setSuccess(true);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-amber-50 to-white">
      <header className="w-full p-4 flex items-center justify-between bg-white/80 backdrop-blur-sm border-b border-orange-100">
        <Button
          variant="ghost"
          className="text-orange-800 hover:text-orange-900"
          onClick={() => navigate("/login")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Tilbake til innlogging
        </Button>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/80 backdrop-blur-sm border-orange-100">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center flex items-center justify-center gap-2 text-gray-900">
              <User className="h-6 w-6 text-orange-800" />
              Magisk lenke innlogging
            </CardTitle>
            <CardDescription className="text-center text-gray-700">
              Logg inn uten passord ved å motta en lenke på e-post
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <Alert className="bg-green-50 border-green-200 mb-4">
                <AlertDescription className="text-green-800">
                  En magisk innloggingslenke har blitt sendt til {email}. 
                  Sjekk innboksen din og klikk på lenken for å logge inn.
                </AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-postadresse</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="din@epost.no"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-orange-800 hover:bg-orange-900 text-white"
                  disabled={loading}
                >
                  {loading ? 'Sender...' : 'Send magisk innloggingslenke'}
                  <Mail className="ml-2 h-4 w-4" />
                </Button>
                
                <div className="text-center text-sm text-gray-600 mt-4">
                  <p>Du vil motta en e-post med en lenke du kan bruke for å logge inn</p>
                </div>
              </form>
            )}
          </CardContent>
          <CardFooter className="flex justify-center">
            {success && (
              <Button 
                variant="outline" 
                onClick={() => navigate("/login")}
                className="mt-2"
              >
                Tilbake til innlogging
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>

      <footer className="w-full p-4 bg-white/80 backdrop-blur-sm border-t border-orange-100 text-center text-gray-700">
        <p>© 2024 AdvoKAI. Alle rettigheter forbeholdt.</p>
      </footer>
    </div>
  );
}
